import React from 'react'
import { LanguageTag } from '@/components/features/spaces/LanguageTag'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ExternalLink, Lock, MoreHorizontal, Pencil, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useRoute } from '@/lib/RouteContext'
import { useSidebar } from '@/lib/SidebarContext'
import { useAuth } from '@/lib/AuthContext'
import { useOptionalCommunity } from '@/lib/CommunityContext'
import { useMenuCounters } from '@/lib/menuCounters'
import { isSafeExternalUrl } from '@/lib/safeUrl'
import { lockReasonKey } from '@/types/access'
import { type Section } from '@/types/sections'
import { type Space } from '@/types/spaces'
import { AppLink } from '@/components/ui/app-link'
import { BottomSheet, useBottomSheet } from '@/components/ui/bottom-sheet'
import { SectionPills, type SectionPill } from '@/components/ui/section-nav'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useChannelUnread } from '@/components/features/channel/channelUnread'
import { useHasCookieChoice } from '@/components/features/cookies/useHasCookieChoice'
import { openCookieNotice } from '@/lib/cookieConsent'
import { getIconComponent, isRouteActive, useAdminItems } from './Sidebar'
import { MentionBadge, NewsBadge } from './menuBadges'
import {
  RailBadge,
  RailSpaceItem,
  spaceIconOf,
  useActiveSpaceId,
  useOrderedSections,
  useSectionActive,
  useSectionCounters,
} from './SidebarRail'

/**
 * F-191 phase 5: the bar on top, the community's alternative to the side menu.
 * - From 1024 px (`TopNavBar`, in the header): each section is an item that
 *   opens its spaces in a dropdown (keyboard and screen reader as the rail's
 *   floating menu); sections that do not fit go to "Mais"; Administration is
 *   the last item, for whom has a role. A section with one space is that space.
 * - Below 1024 px (`TopNavStrip`, under the header): a strip of section pills
 *   that scrolls sideways; a pill opens its spaces in a bottom sheet.
 * The counters of phases 2 and 2b ride on the section item / pill.
 */

const externalOf = (space: Space) => (space.externalUrl && isSafeExternalUrl(space.externalUrl) ? String(space.externalUrl).trim() : null)
const spacePath = (section: Section, space: Space) => `/${section.slug}/${space.slug}`

/** The section's own icon (none for the default folder: the label says it). */
const SectionIcon = ({ section, className }: { section: Section; className?: string }) => {
  if (!section.icon || section.icon === 'folder') return null
  const Icon = getIconComponent(section.icon)
  return <Icon className={cn('h-4 w-4 flex-shrink-0', className)} aria-hidden="true" />
}

const barItemClass = (active: boolean) => cn(
  'relative inline-flex h-10 max-w-[14rem] flex-shrink-0 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
  active ? 'bg-primary-50 text-primary' : 'text-foreground-secondary hover:bg-muted hover:text-foreground'
)

/* ------------------------------------------------------------------ */
/* Desktop                                                             */
/* ------------------------------------------------------------------ */

/** One section in the header: a link (none or one space) or a dropdown with its spaces. */
const BarSection = ({ section, activeSpaceId }: { section: Section; activeSpaceId: string | null }) => {
  const { t } = useTranslation(['navigation', 'common'])
  const spaces = section.spaces ?? []
  const active = useSectionActive(section, activeSpaceId)
  const counters = useSectionCounters(section)
  const sectionLockKey = section.locked ? lockReasonKey(section.lockReason) : null
  const badge = <RailBadge {...counters} />
  const body = (
    <>
      <SectionIcon section={section} />
      <span className="min-w-0 truncate">{section.title}</span>
      <LanguageTag languages={section.languages} />
      {sectionLockKey && <Lock className="h-3 w-3 flex-shrink-0 text-[rgb(var(--rgb-gray-400))]" aria-label={t(sectionLockKey)} />}
    </>
  )

  if (spaces.length === 0) {
    return (
      <AppLink to={`/${section.slug}`} aria-current={active ? 'page' : undefined} className={barItemClass(active)} data-top-section={section.id}>
        {body}{badge}
      </AppLink>
    )
  }

  if (spaces.length === 1) {
    const [space] = spaces
    if (space.module === 'url' && !space.locked) {
      return (
        <a href={externalOf(space) ?? undefined} target="_blank" rel="noopener noreferrer" className={barItemClass(false)} data-top-section={section.id}>
          {body}<ExternalLink className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
        </a>
      )
    }
    return (
      <AppLink to={spacePath(section, space)} aria-current={active ? 'page' : undefined} className={barItemClass(active)} data-top-section={section.id}>
        {body}{badge}
      </AppLink>
    )
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button type="button" className={barItemClass(active)} data-top-section={section.id} data-active={active ? 'true' : undefined}>
          {body}
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 opacity-70" aria-hidden="true" />
          {badge}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={8} className="max-h-[min(70vh,32rem)] w-64 overflow-y-auto" data-top-menu={section.id}>
        <DropdownMenuLabel className="truncate text-xs font-semibold uppercase tracking-wider text-muted-foreground">{section.title}</DropdownMenuLabel>
        {spaces.map((space) => (
          <RailSpaceItem key={space.id} space={space} section={section} active={space.id === activeSpaceId} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** "Mais": the sections that did not fit, each with its spaces under its name. */
const MoreMenu = ({ sections, activeSpaceId }: { sections: Section[]; activeSpaceId: string | null }) => {
  const { t } = useTranslation('navigation')
  const counters = useMenuCounters()
  const active = sections.some((section) => (section.spaces ?? []).some((space) => space.id === activeSpaceId))
  const news = sections.reduce((sum, section) => sum + (counters.sections[section.id] ?? 0), 0)
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button type="button" className={barItemClass(active)} data-top-more>
          <MoreHorizontal className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{t('topNav.more')}</span>
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 opacity-70" aria-hidden="true" />
          {news > 0 && <NewsBadge count={news} className="absolute -right-1 -top-1 h-4 min-w-[1rem] px-1 text-[10px]" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="max-h-[min(75vh,36rem)] w-64 overflow-y-auto" data-top-more-menu>
        {sections.map((section, index) => {
          const spaces = section.spaces ?? []
          return (
            <React.Fragment key={section.id}>
              {index > 0 && <DropdownMenuSeparator />}
              {spaces.length === 0 ? (
                <DropdownMenuItem asChild className="gap-2">
                  <AppLink to={`/${section.slug}`}><SectionIcon section={section} /><span className="truncate">{section.title}</span></AppLink>
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuLabel className="truncate text-xs font-semibold uppercase tracking-wider text-muted-foreground">{section.title}</DropdownMenuLabel>
                  {spaces.map((space) => (
                    <RailSpaceItem key={space.id} space={space} section={section} active={space.id === activeSpaceId} />
                  ))}
                </>
              )}
            </React.Fragment>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Administração, last, for whom has a role; "Editar menu" brings the side menu back for owner and admin. */
const AdminMenu = () => {
  const { t } = useTranslation('navigation')
  const { currentPath } = useRoute()
  const { hasRole } = useAuth()
  const { setMenuEditing } = useSidebar()
  const items = useAdminItems().filter((item) => item.visible)
  const active = items.some((item) => isRouteActive(currentPath, item.path))
  const canEdit = hasRole(['owner', 'admin'])
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button type="button" className={barItemClass(active)} data-top-admin>
          <ShieldCheck className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{t('administration')}</span>
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 opacity-70" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="max-h-[min(80vh,36rem)] w-60 overflow-y-auto">
        {items.map((item) => {
          const itemActive = isRouteActive(currentPath, item.path)
          return (
            <DropdownMenuItem key={item.path} asChild className={cn('gap-2', itemActive && 'bg-primary-50 text-primary')}>
              <AppLink to={item.path} aria-current={itemActive ? 'page' : undefined}>
                <item.icon className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </AppLink>
            </DropdownMenuItem>
          )
        })}
        {canEdit && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2" onSelect={() => setMenuEditing(true)} data-top-edit-menu>
              <Pencil className="h-4 w-4 flex-shrink-0" />
              <span>{t('sidebar.editMenu')}</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Width the "Mais" item takes (measured once rendered; this is the first guess). */
const MORE_WIDTH = 96
const GAP = 4

export const TopNavBar = ({ className }: { className?: string }) => {
  const { t } = useTranslation('navigation')
  const { isAuthenticated } = useAuth()
  const sections = useOrderedSections()
  const activeSpaceId = useActiveSpaceId()
  const hasAdmin = useAdminItems().some((item) => item.visible) && isAuthenticated
  const container = React.useRef<HTMLDivElement>(null)
  const measureRow = React.useRef<HTMLDivElement>(null)
  const [fitting, setFitting] = React.useState(sections.length)

  /* How many sections fit before "Mais" and Administração: each item's width
     comes from an invisible copy of the row, the room from the bar itself. */
  const layout = React.useCallback(() => {
    const box = container.current
    const row = measureRow.current
    if (!box || !row) return
    const widths = Array.from(row.querySelectorAll<HTMLElement>('[data-measure-section]')).map((node) => node.offsetWidth)
    const adminWidth = row.querySelector<HTMLElement>('[data-measure-admin]')?.offsetWidth ?? 0
    const moreWidth = row.querySelector<HTMLElement>('[data-measure-more]')?.offsetWidth ?? MORE_WIDTH
    const room = box.clientWidth - (adminWidth ? adminWidth + GAP : 0)
    const total = widths.reduce((sum, width) => sum + width + GAP, 0)
    if (total <= room) { setFitting(widths.length); return }
    let used = moreWidth + GAP
    let count = 0
    for (const width of widths) {
      if (used + width + GAP > room) break
      used += width + GAP
      count += 1
    }
    setFitting(count)
  }, [])

  React.useLayoutEffect(() => {
    layout()
    const box = container.current
    if (!box || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(layout)
    observer.observe(box)
    return () => observer.disconnect()
  }, [layout, sections, hasAdmin])

  const shown = sections.slice(0, fitting)
  const rest = sections.slice(fitting)

  return (
    <div ref={container} className={cn('relative min-w-0', className)} data-top-nav>
      <nav aria-label={t('topNav.label')} className="flex min-w-0 items-center gap-1">
        {shown.map((section) => <BarSection key={section.id} section={section} activeSpaceId={activeSpaceId} />)}
        {rest.length > 0 && <MoreMenu sections={rest} activeSpaceId={activeSpaceId} />}
        {hasAdmin && <AdminMenu />}
      </nav>
      {/* The invisible copy the widths are read from (same classes, no menus). */}
      <div ref={measureRow} aria-hidden="true" className="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-1" style={{ width: 'max-content' }}>
        {sections.map((section) => (
          <span key={section.id} className={barItemClass(false)} data-measure-section>
            <SectionIcon section={section} />
            <span className="min-w-0 truncate">{section.title}</span>
            <LanguageTag languages={section.languages} />
            {(section.spaces?.length ?? 0) > 1 && <ChevronDown className="h-3.5 w-3.5" />}
          </span>
        ))}
        <span className={barItemClass(false)} data-measure-more><MoreHorizontal className="h-4 w-4" /><span>{t('topNav.more')}</span><ChevronDown className="h-3.5 w-3.5" /></span>
        {hasAdmin && <span className={barItemClass(false)} data-measure-admin><ShieldCheck className="h-4 w-4" /><span>{t('administration')}</span><ChevronDown className="h-3.5 w-3.5" /></span>}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Phone                                                               */
/* ------------------------------------------------------------------ */

const sheetRowClass = (active: boolean) => cn(
  'flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
  active ? 'bg-primary-50 font-medium text-primary-700' : 'text-foreground hover:bg-muted'
)

/** A space in the section's sheet: icon, name, its counter and the padlock. */
const SheetSpaceRow = ({ space, section, active, onGo }: { space: Space; section: Section; active: boolean; onGo: (path: string) => void }) => {
  const { t } = useTranslation(['navigation', 'common'])
  const Icon = spaceIconOf(space)
  const channel = useChannelUnread(space.module === 'channel' ? space.id : undefined)
  const mentions = channel?.mentions ?? 0
  const news = useMenuCounters().spaces[space.id] ?? 0
  const lockKey = space.locked ? lockReasonKey(space.lockReason) ?? 'common:access.lockReason.product' : null
  const content = (
    <>
      <Icon className="h-5 w-5 flex-shrink-0 text-[rgb(var(--rgb-gray-500))]" aria-hidden="true" />
      <span className={cn('min-w-0 flex-1 truncate', channel?.hasUnread && 'font-semibold')}>{space.title}</span>
      <LanguageTag languages={space.languages} />
      {mentions > 0 ? <MentionBadge count={mentions} /> : news > 0 && !active ? <NewsBadge count={news} /> : null}
      {space.module === 'url' && !space.locked && <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
      {lockKey && <Lock className="h-3.5 w-3.5 flex-shrink-0 text-[rgb(var(--rgb-gray-400))]" aria-label={t(lockKey)} />}
    </>
  )
  if (space.module === 'url' && !space.locked) {
    const external = externalOf(space)
    return <li><a href={external ?? undefined} target="_blank" rel="noopener noreferrer" className={sheetRowClass(false)}>{content}</a></li>
  }
  return (
    <li>
      <button type="button" className={sheetRowClass(active)} aria-current={active ? 'page' : undefined} onClick={() => onGo(spacePath(section, space))} data-sheet-space={space.id}>
        {content}
      </button>
    </li>
  )
}

const ADMIN_KEY = '__admin'

/** The pill's corner mark: mentions (red), then news, then a dot for an unread channel. */
const PillBadge = ({ mentions, news, unread }: { mentions: number; news: number; unread: boolean }) => {
  if (mentions > 0) return <MentionBadge count={mentions} className="h-4 min-w-[1rem] px-1 text-[10px]" />
  if (news > 0) return <NewsBadge count={news} className="h-4 min-w-[1rem] px-1 text-[10px]" />
  if (unread) return <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary ring-2 ring-surface" data-channel-unread="true" aria-hidden="true" />
  return null
}

/** Reads one section's counters for its pill (hooks need a component per section). */
const PillCounters = ({ section, onChange }: { section: Section; onChange: (id: string, value: { mentions: number; news: number; unread: boolean }) => void }) => {
  const counters = useSectionCounters(section)
  React.useEffect(() => { onChange(section.id, counters) }, [section.id, counters.mentions, counters.news, counters.unread]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

export const TopNavStrip = () => {
  const { t } = useTranslation('navigation')
  const { navigate, currentPath } = useRoute()
  const { isAuthenticated, hasRole } = useAuth()
  const { setMenuEditing } = useSidebar()
  const sections = useOrderedSections()
  const activeSpaceId = useActiveSpaceId()
  const adminItems = useAdminItems().filter((item) => item.visible)
  const sheet = useBottomSheet()
  const [openKey, setOpenKey] = React.useState<string | null>(null)
  const [counters, setCounters] = React.useState<Record<string, { mentions: number; news: number; unread: boolean }>>({})
  const onCounters = React.useCallback((id: string, value: { mentions: number; news: number; unread: boolean }) => {
    setCounters((prev) => (prev[id]?.mentions === value.mentions && prev[id]?.news === value.news && prev[id]?.unread === value.unread ? prev : { ...prev, [id]: value }))
  }, [])

  const routeSectionSlug = currentPath.split(/[?#]/)[0].split('/').filter(Boolean)[0]
  const isActive = (section: Section) => routeSectionSlug === section.slug || (section.spaces ?? []).some((space) => space.id === activeSpaceId)
  const showAdmin = isAuthenticated && adminItems.length > 0
  const adminActive = adminItems.some((item) => isRouteActive(currentPath, item.path))

  const pills: SectionPill[] = sections.map((section) => {
    const count = counters[section.id]
    return {
      key: section.id,
      label: section.title,
      icon: <SectionIcon section={section} />,
      badge: count ? <PillBadge {...count} /> : undefined,
      active: isActive(section),
      opensSheet: (section.spaces?.length ?? 0) > 1,
      testId: `top-pill-${section.slug}`,
    }
  })
  if (showAdmin) pills.push({ key: ADMIN_KEY, label: t('administration'), icon: <ShieldCheck className="h-4 w-4 flex-shrink-0" aria-hidden="true" />, active: adminActive, opensSheet: true, testId: 'top-pill-admin' })

  const select = (key: string) => {
    if (key === ADMIN_KEY) { setOpenKey(key); sheet.show(); return }
    const section = sections.find((item) => item.id === key)
    if (!section) return
    const spaces = section.spaces ?? []
    /* No spaces: the section's page. One space: straight to it (two taps at most, §4.5.2 crit. 1). */
    if (spaces.length === 0) { navigate(`/${section.slug}`); return }
    if (spaces.length === 1) {
      const [space] = spaces
      if (space.module === 'url' && !space.locked) {
        const external = externalOf(space)
        if (external) window.open(external, '_blank', 'noopener,noreferrer')
        return
      }
      navigate(spacePath(section, space))
      return
    }
    setOpenKey(key)
    sheet.show()
  }

  const go = (path: string) => sheet.hide(() => navigate(path))
  const openSection = openKey && openKey !== ADMIN_KEY ? sections.find((item) => item.id === openKey) ?? null : null
  const sheetTitle = openKey === ADMIN_KEY ? t('administration') : openSection?.title ?? ''

  return (
    <div className="theme-region-header fixed inset-x-0 top-16 z-40 h-12 border-b border-border bg-surface lg:hidden" data-top-strip>
      {sections.map((section) => <PillCounters key={section.id} section={section} onChange={onCounters} />)}
      <SectionPills items={pills} onSelect={select} aria-label={t('topNav.label')} className="h-full" data-testid="top-strip" />
      <BottomSheet sheet={sheet} title={sheetTitle} data-testid="top-strip-sheet">
        <div className="px-3 pb-2">
          <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" aria-hidden="true">{sheetTitle}</p>
          {openSection && (
            <ul className="space-y-0.5">
              {(openSection.spaces ?? []).map((space) => (
                <SheetSpaceRow key={space.id} space={space} section={openSection} active={space.id === activeSpaceId} onGo={go} />
              ))}
            </ul>
          )}
          {openKey === ADMIN_KEY && (
            <ul className="space-y-0.5">
              {adminItems.map((item) => {
                const active = isRouteActive(currentPath, item.path)
                return (
                  <li key={item.path}>
                    <button type="button" className={sheetRowClass(active)} aria-current={active ? 'page' : undefined} onClick={() => go(item.path)}>
                      <item.icon className="h-5 w-5 flex-shrink-0 text-[rgb(var(--rgb-gray-500))]" aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    </button>
                  </li>
                )
              })}
              {hasRole(['owner', 'admin']) && (
                <li className="mt-1 border-t border-border pt-1">
                  <button type="button" className={sheetRowClass(false)} onClick={() => sheet.hide(() => setMenuEditing(true))} data-top-edit-menu>
                    <Pencil className="h-5 w-5 flex-shrink-0 text-[rgb(var(--rgb-gray-500))]" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">{t('sidebar.editMenu')}</span>
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      </BottomSheet>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Footer                                                              */
/* ------------------------------------------------------------------ */

/**
 * What the side menu carries at its foot, with the bar on top: the cookie
 * notice (F-201) and the plan's "Powered by Memberfy" mark (F-34).
 */
export const TopNavFooter = () => {
  const { t } = useTranslation(['common', 'entitlements'])
  const community = useOptionalCommunity()
  const hasCookieChoice = useHasCookieChoice()
  const mark = community?.branding?.showMemberfyMark === true
  if (!hasCookieChoice && !mark) return null
  return (
    <footer className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 px-4 pb-6 pt-2 text-xs text-[rgb(var(--rgb-gray-400))]" data-top-footer>
      {hasCookieChoice && (
        <button type="button" onClick={openCookieNotice} className="transition-colors hover:text-foreground-secondary" data-testid="sidebar-cookies">
          {t('common:cookies.link')}
        </button>
      )}
      {mark && (
        <a href="https://memberfy.net" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-foreground-secondary" data-testid="memberfy-mark">
          {t('entitlements:poweredBy')}
        </a>
      )}
    </footer>
  )
}
